import { Head } from '@inertiajs/react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import SyntaxHighlighter from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'

export default function JavaScriptEvents() {
  return (
    <div className="container mx-auto py-6 px-4 max-w-6xl">
      <Head title="JavaScript Events - Documentation" />
      
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">JavaScript Events</h1>
        <p className="text-lg text-gray-600 mb-4">
          The uploader emits custom JavaScript events on <code>&lt;input&gt;.change</code>
        </p>
        
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Important:</strong> The URL returned from the uploader is the <code className="bg-blue-100 px-1 py-0.5 rounded text-blue-800">cdnUrl</code>. This is the string that you should save to your database to access the uploaded file later in your application.
              </p>
            </div>
          </div>
        </div>
      </div>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Event Listening</CardTitle>
          <CardDescription>
            The uploader emits a single 'change' event with different actions in the event detail when the user does something. 
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <p className="text-gray-600 mb-3">
              The uploader uses a single 'change' event to notify you about all uploader actions. The specific action type is available in <code>event.detail.action</code>.
            </p>
            
            <div className="bg-gray-100 p-4 rounded-lg overflow-x-auto mb-6">
              <h3 className="text-lg font-semibold mb-2">Basic Implementation</h3>
              <SyntaxHighlighter
                language="html"
                style={atomOneDark}
                customStyle={{
                  background: '#21252b',
                  padding: '1rem',
                  margin: 0,
                  fontSize: '.8rem',
                  borderRadius: '6px'
                }}
              >
                {`<script>
  document.addEventListener('DOMContentLoaded', function() {
    // Find the uploader element
    const uploader = document.querySelector('simple-file-upload');
    if (uploader) {
      // Add an event listener for the change event
      uploader.addEventListener('change', function(e) {
        console.log('Action:', e.detail.action);
        console.log('All Files:', e.detail.allFiles);
        console.log('Full Detail:', e.detail);
      });
    }
  });
</script>`}
              </SyntaxHighlighter>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">Event Actions</h3>
            <p className="text-gray-600 mb-4">
              The 'change' event will fire with different actions in the <code>event.detail</code> object:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium">File Uploaded</h4>
                  <Badge variant="outline" className="text-green-600 border-green-600">fileUploaded</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Fired when a file is successfully uploaded. The event detail contains information about the uploaded file.
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium">File Removed</h4>
                  <Badge variant="outline" className="text-red-600 border-red-600">fileRemoved</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Fired when a file is removed from the uploader.
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium">Upload Done</h4>
                  <Badge variant="outline" className="text-blue-600 border-blue-600">uploadDone</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Fired when the user clicks the "Done" button in the multi-file uploader interface.
                </p>
              </div>
              
              <div className="border rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <h4 className="font-medium">Upload Failed</h4>
                  <Badge variant="outline" className="text-red-600 border-red-600">fileUploadFailed</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Fired when a file upload fails. The event detail contains error information.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Event Detail Examples</CardTitle>
          <CardDescription>
            Sample responses for each event action
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              File Uploaded Event
              <Badge variant="outline" className="text-green-600 border-green-600">fileUploaded</Badge>
            </h4>
            <SyntaxHighlighter
              language="json"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`{
  action: "fileUploaded",
  allFiles: [
    {
      cdnUrl: "https://cdn-3oykw3z0.files-simplefileupload.com/static/blobs/proxy/eyJfcmFpbHMi...",
      error: null,
      name: "donuts.jpg",
      size: 4722094,
      status: "success",
      type: "image/jpeg",
    }
  ]
}`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              Upload Done Event
              <Badge variant="outline" className="text-blue-600 border-blue-600">uploadDone</Badge>
            </h4>
            <SyntaxHighlighter
              language="json"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`{
  action: "uploadDone",
  allFiles: [
    {
      cdnUrl: "https://cdn-3oykw3z0.files-simplefileupload.com/static/blobs/proxy/eyJfcmFpbHMi...",
      error: null,
      name: "donuts.jpg",
      size: 4722094,
      status: "success",
      type: "image/jpeg",
    }
  ]
}`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              File Removed Event
              <Badge variant="outline" className="text-red-600 border-red-600">fileRemoved</Badge>
            </h4>
            <SyntaxHighlighter
              language="json"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`{
  action: "fileRemoved",
  allFiles: [
    // Remaining files after removal
  ]
}`}
            </SyntaxHighlighter>
          </div>

          <div>
            <h4 className="font-semibold mb-2 flex items-center gap-2">
              Upload Failed Event
              <Badge variant="outline" className="text-red-600 border-red-600">fileUploadFailed</Badge>
            </h4>
            <SyntaxHighlighter
              language="json"
              style={atomOneDark}
              customStyle={{
                background: '#21252b',
                padding: '1rem',
                margin: 0,
                fontSize: '.8rem',
                borderRadius: '6px'
              }}
            >
              {`{
  action: "fileUploadFailed",
  allFiles: [
    {
      error: "File size exceeds limit",
      name: "large-file.jpg",
      status: "error",
      // ... other file properties
    }
  ]
}`}
            </SyntaxHighlighter>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Complete Example</CardTitle>
          <CardDescription>
            A full example showing how to handle all events
          </CardDescription>
        </CardHeader>
        <CardContent>
          <SyntaxHighlighter
            language="html"
            style={atomOneDark}
            customStyle={{
              background: '#21252b',
              padding: '1rem',
              margin: 0,
              fontSize: '.8rem',
              borderRadius: '6px'
            }}
          >
            {`<simple-file-upload public-key="yourPublicKey"></simple-file-upload>
<script type="module" src="/src/simple-file-upload.js"></script>
<script>
  // Listen for change events on the uploader
  document.addEventListener('DOMContentLoaded', function() {
    const uploader = document.querySelector('simple-file-upload');
    if (uploader) {
      console.log('found')
      uploader.addEventListener('change', function(e) {
        alert(
          'Uploader action: ' + e.detail.action + '\\n' +
          'Number of files: ' + (e.detail.allFiles ? e.detail.allFiles.length : 0) + '\\n' +
          'Detail: ' + JSON.stringify(e.detail, null, 2)
        );
        console.log(e.detail);
      });
    }
  });
</script>
`}
          </SyntaxHighlighter>
        </CardContent>
      </Card>
    </div>
  );
}
